import * as React from 'react';

import '@mui/lab/themeAugmentation';

import { Shadows } from '@mui/material/styles/shadows';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import AdapterLuxon from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

import { CerebroSnackbarProvider } from './CerebroSnackbarProvider';

export const CerebroThemeProvider: React.FC = ({ children }) => {
	return (
		<ThemeProvider theme={theme}>
			<LocalizationProvider dateAdapter={AdapterLuxon}>
				<CssBaseline />
				<CerebroSnackbarProvider>{children}</CerebroSnackbarProvider>
			</LocalizationProvider>
		</ThemeProvider>
	);
};

export const weights = { bold: 700, semibold: 600, medium: 500, regular: 400 };

const colors = {
	yellow: '#FFCF00',
	green: '#548235',

	darkGray: '#3F3F3F',
	mediumGray: '#75767A',
	lightGray: '#E9E9E9',
	backgroundGray: '#F8F8F8',

	white: '#FFFFFF',
	borderGray: 'rgba(224, 224, 224, 1)',
};

export const fontFamily = 'Montserrat, sans-serif;';

const theme = createTheme({
	palette: {
		primary: {
			main: colors.yellow,
		},

		background: {
			default: colors.white,
			paper: colors.white,
		},

		gray: {
			background: colors.backgroundGray,
			light: colors.lightGray,
			medium: colors.mediumGray,
			dark: colors.darkGray,
			border: colors.borderGray,
		},

		green: {
			main: colors.green,
		},
	},

	shape: {
		borderRadius: 5,
	},

	typography: {
		fontFamily: fontFamily,
		h1: {
			fontSize: 24,
			lineHeight: '28px',
			fontWeight: weights.bold,
			color: colors.darkGray,
		},
		h2: {
			fontSize: 18,
			lineHeight: '22px',
			fontWeight: weights.bold,
			color: colors.darkGray,
		},
		h3: {
			fontSize: 14,
			lineHeight: '18px',
			fontWeight: weights.bold,
			color: colors.darkGray,
		},
		body1: {
			fontSize: 16,
			lineHeight: '22px',
			fontWeight: weights.regular,
		},

		body2: {
			fontSize: 14,
			lineHeight: '20px',
			fontWeight: weights.regular,
		},

		body3: {
			fontSize: 12,
			lineHeight: '14px',
			fontWeight: weights.regular,
			fontFamily: fontFamily,
		},

		labelLarge: {
			display: 'block',
			fontSize: 14,
			lineHeight: '18px',
			fontWeight: weights.semibold,
			fontFamily: fontFamily,
		},

		labelSmall: {
			display: 'block',
			fontSize: 12,
			lineHeight: '14px',
			fontWeight: weights.medium,
			fontFamily: fontFamily,
		},

		cardContentHeading: {
			display: 'block',
			fontSize: 14,
			lineHeight: '12px',
			fontWeight: weights.bold,
			fontFamily: fontFamily,
			textDecoration: 'underline',
			textTransform: 'uppercase',
		},

		data: {
			fontSize: 24,
			lineHeight: '24px',
			fontWeight: weights.medium,
			fontFamily: fontFamily,
		},

		hint: {
			fontSize: 12,
			lineHeight: '14px',
			fontWeight: weights.medium,
			fontFamily: fontFamily,
		},

		breadcrumb: {
			fontSize: 10,
			lineHeight: '28px',
			fontWeight: weights.regular,
			fontFamily: fontFamily,
			textTransform: 'uppercase',
		},

		link: {
			textDecoration: 'none !important',
			cursor: 'pointer',
			fontSize: 14,
			lineHeight: '20px',
			fontWeight: weights.medium,
			fontFamily: fontFamily,
		},
	},

	yellowShadows: [
		'none',
		createYellowShadow(0, 2, 1, -1, 0, 1, 1, 0, 0, 1, 3, 0),
		createYellowShadow(0, 3, 1, -2, 0, 2, 2, 0, 0, 1, 5, 0),
		createYellowShadow(0, 3, 3, -2, 0, 3, 4, 0, 0, 1, 8, 0),
		createYellowShadow(0, 2, 4, -1, 0, 4, 5, 0, 0, 1, 10, 0),
		createYellowShadow(0, 3, 5, -1, 0, 5, 8, 0, 0, 1, 14, 0),
		createYellowShadow(0, 3, 5, -1, 0, 6, 10, 0, 0, 1, 18, 0),
		createYellowShadow(0, 4, 5, -2, 0, 7, 10, 1, 0, 2, 16, 1),
		createYellowShadow(0, 5, 5, -3, 0, 8, 10, 1, 0, 3, 14, 2),
		createYellowShadow(0, 5, 6, -3, 0, 9, 12, 1, 0, 3, 16, 2),
		createYellowShadow(0, 6, 6, -3, 0, 10, 14, 1, 0, 4, 18, 3),
		createYellowShadow(0, 6, 7, -4, 0, 11, 15, 1, 0, 4, 20, 3),
		createYellowShadow(0, 7, 8, -4, 0, 12, 17, 2, 0, 5, 22, 4),
		createYellowShadow(0, 7, 8, -4, 0, 13, 19, 2, 0, 5, 24, 4),
		createYellowShadow(0, 7, 9, -4, 0, 14, 21, 2, 0, 5, 26, 4),
		createYellowShadow(0, 8, 9, -5, 0, 15, 22, 2, 0, 6, 28, 5),
		createYellowShadow(0, 8, 10, -5, 0, 16, 24, 2, 0, 6, 30, 5),
		createYellowShadow(0, 8, 11, -5, 0, 17, 26, 2, 0, 6, 32, 5),
		createYellowShadow(0, 9, 11, -5, 0, 18, 28, 2, 0, 7, 34, 6),
		createYellowShadow(0, 9, 12, -6, 0, 19, 29, 2, 0, 7, 36, 6),
		createYellowShadow(0, 10, 13, -6, 0, 20, 31, 3, 0, 8, 38, 7),
		createYellowShadow(0, 10, 13, -6, 0, 21, 33, 3, 0, 8, 40, 7),
		createYellowShadow(0, 10, 14, -6, 0, 22, 35, 3, 0, 8, 42, 7),
		createYellowShadow(0, 11, 14, -7, 0, 23, 36, 3, 0, 9, 44, 8),
		createYellowShadow(0, 11, 15, -7, 0, 24, 38, 3, 0, 9, 46, 8),
	],

	solidShadow: (color, thickness = 2) => `${thickness}px ${thickness}px 0px 0px ${color}`,
	solidShadowAll: (color, thickness = 2) => `${thickness}px ${thickness}px ${thickness}px ${thickness}px ${color}`,

	components: {
		MuiAlert: {
			styleOverrides: {
				root: {
					border: '10px solid blue',
				},
				filledSuccess: {
					backgroundColor: colors.yellow,
				},
				standardSuccess: {
					backgroundColor: colors.yellow,
				},
			},
		},
	},
});

declare module '@mui/material/styles' {
	interface Theme {
		yellowShadows: Shadows;
		solidShadow: (color: string, thickness?: number) => string;
		solidShadowAll: (color: string, thickness?: number) => string;
	}

	interface ThemeOptions {
		yellowShadows?: Shadows;
		solidShadow: (color: string, thickness?: number) => string;
		solidShadowAll: (color: string, thickness?: number) => string;
	}

	interface Palette {
		gray: {
			background: string;
			light: string;
			medium: string;
			dark: string;
			border: string;
		};
		green: {
			main: string;
		};
	}

	interface PaletteOptions {
		gray: {
			background: string;
			light: string;
			medium: string;
			dark: string;
			border: string;
		};
		green: {
			main: string;
		};
	}

	interface TypographyVariants {
		body3: React.CSSProperties;
		labelLarge: React.CSSProperties;
		labelSmall: React.CSSProperties;
		data: React.CSSProperties;
		hint: React.CSSProperties;
		breadcrumb: React.CSSProperties;
		cardContentHeading: React.CSSProperties;
		link: React.CSSProperties;
	}

	// allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		body3: React.CSSProperties;
		labelLarge: React.CSSProperties;
		labelSmall: React.CSSProperties;
		data: React.CSSProperties;
		hint: React.CSSProperties;
		breadcrumb: React.CSSProperties;
		cardContentHeading: React.CSSProperties;
		link: React.CSSProperties;
	}
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		body3: true;
		labelLarge: true;
		labelSmall: true;
		data: true;
		hint: true;
		breadcrumb: true;
		h4: false;
		h5: false;
		h6: false;
		cardContentHeading: true;
		link: true;
	}
}

// Pulled from https://github.com/mui-org/material-ui/blob/master/packages/mui-material/src/styles/shadows.js
function createYellowShadow(...px: any[]) {
	return [
		`${px[0]}px ${px[1]}px ${px[2]}px ${px[3]}px rgba(255, 207, 0,0.2)`,
		`${px[4]}px ${px[5]}px ${px[6]}px ${px[7]}px rgba(255, 207, 0,0.14)`,
		`${px[8]}px ${px[9]}px ${px[10]}px ${px[11]}px rgba(255, 207, 0,0.12)`,
	].join(',');
}
